import type { CartItem } from '../../types'
import { CUSTOMER_TYPE_LABELS } from './customerTypeLabels'

interface OrderReviewModalProps {
  isOpen: boolean
  onClose: () => void
  onEdit: () => void
  onComplete: () => void
  outletName: string
  cart: CartItem[]
  total: number
  formatRupiah: (amount: number) => string
  isSubmitting?: boolean
  submitError?: string | null
}

export function OrderReviewModal({
  isOpen,
  onClose,
  onEdit,
  onComplete,
  outletName,
  cart,
  total,
  formatRupiah,
  isSubmitting = false,
  submitError = null,
}: OrderReviewModalProps) {
  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="review-title">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="review-title" className="modal-title">Nota Pesanan</h2>
          <button onClick={onClose} className="modal-close-btn" aria-label="Tutup">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-brand">
            <svg className="modal-brand-logo" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <rect width="32" height="32" rx="8" fill="currentColor" />
              <path d="M8 12h16M8 16h12M8 20h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="modal-brand-name">CV Berkah Nusa Sejati</span>
          </div>

          <div className="modal-section">
            <p className="modal-section-label">Nama Warung / Toko</p>
            <div className="modal-outlet-box">
              <p className="modal-outlet-name">{outletName}</p>
            </div>
          </div>

          <div className="modal-section">
            <p className="modal-section-label">Item Pesanan</p>
            {cart.map((item) => (
              <div key={`${item.productId}-${item.purchaseType}`} className="modal-order-item">
                <p className="modal-order-item-name">{item.productName}</p>
                <div className="modal-order-item-details">
                  <div className="modal-order-item-meta">
                    <span className="modal-order-item-type">{CUSTOMER_TYPE_LABELS[item.purchaseType]}</span>
                    <span className="modal-order-item-qty">× {item.quantity}</span>
                  </div>
                  <div className="modal-order-item-pricing">
                    <span className="modal-order-item-unit-price">{formatRupiah(item.unitPrice)} / {CUSTOMER_TYPE_LABELS[item.purchaseType]}</span>
                    <span className="modal-order-item-subtotal">{formatRupiah(item.subtotal)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-total-section">
            <div className="modal-total-row">
              <span className="modal-total-label">Subtotal</span>
              <span className="modal-total-value">{formatRupiah(total)}</span>
            </div>
            <div className="modal-total-row modal-total-row--grand">
              <span className="modal-total-label">Total</span>
              <span className="modal-total-value">{formatRupiah(total)}</span>
            </div>
          </div>

          <p className="modal-readonly-hint">Modal ini hanya untuk tinjauan. Tekan "Edit Pesanan" untuk mengubah kuantitas atau menghapus item.</p>

          {submitError && (
            <p className="error-message modal-submit-error" role="alert">
              {submitError}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={onEdit}
            disabled={isSubmitting}
            className="btn btn-secondary btn-full modal-btn-edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            ← Edit Pesanan
          </button>
          <button
            onClick={onComplete}
            disabled={isSubmitting}
            className="btn btn-primary btn-full modal-btn-complete"
            aria-busy={isSubmitting}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {isSubmitting ? 'Memproses...' : '✓ Selesaikan Pesanan'}
          </button>
        </div>
      </div>
    </div>
  )
}